import { Link, useParams } from "wouter";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarIcon, MapPinIcon, PackageIcon } from "lucide-react";
import { format } from "date-fns";
import DashboardLayout from "@/components/DashboardLayout";
import { useTranslation } from "react-i18next";

export default function UserProfile() {
  const params = useParams();
  const userId = params.id as Id<"users">;
  const { t } = useTranslation();

  // Fetch Public Profile Data
  const userProfile = useQuery(api.users.getPublicProfile, { userId });
  const userAds = useQuery(api.ads.getAdsByUser, { userId });
  const userStats = useQuery(api.users.getUserStats, { userId });

  if (userProfile === undefined) {
    return <ProfileSkeleton />;
  }

  if (userProfile === null) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
          <h2 className="text-2xl font-bold">{t('user.notFound')}</h2>
          <p className="text-muted-foreground">{t('user.notFoundDesc')}</p>
          <Link href="/">
            <a className="text-primary hover:underline">{t('user.goHome')}</a>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Left Sidebar: User Info */}
          <div className="md:col-span-1 space-y-6">
            <Card>
              <CardContent className="pt-6 flex flex-col items-center text-center space-y-4">
                <Avatar className="h-32 w-32 border-4 border-background shadow-xl">
                  <AvatarImage src={userProfile.profileImage} alt={userProfile.name} />
                  <AvatarFallback className="text-4xl bg-primary/10 text-primary">
                    {userProfile.name?.charAt(0).toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">
                    {userProfile.name || t('user.anonymous')}
                  </h1>
                  <p className="text-sm text-muted-foreground break-all">
                    @{userId}
                  </p>
                </div>

                <div className="w-full flex justify-center py-2">
                   <Badge variant="outline" className="px-3 py-1 bg-secondary/50">
                     {t('user.verified')}
                   </Badge>
                </div>

                <div className="w-full pt-4 border-t space-y-3 text-sm text-left">
                  <div className="flex items-center text-muted-foreground">
                    <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
                    <span>{t('user.joined', { date: new Date(userProfile.createdAt || Date.now()).toLocaleDateString() })}</span>
                  </div>
                  {userStats && (
                    <div className="flex items-center text-muted-foreground">
                      <PackageIcon className="mr-2 h-4 w-4 opacity-70" />
                      <span>{t('user.activeListings', { count: userStats.activeAdsCount })}</span>
                    </div>
                  )}
                  {/* Placeholder for location if added later */}
                  {/* <div className="flex items-center text-muted-foreground">
                    <MapPinIcon className="mr-2 h-4 w-4 opacity-70" />
                    <span>Location Unknown</span>
                  </div> */}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('user.about')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {userProfile.bio || t('user.noBio')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Right Main Content: User Listings */}
          <div className="md:col-span-3 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold tracking-tight">{t('user.listings')}</h2>
              <Badge variant="secondary" className="text-sm">
                {t('user.items', { count: userAds?.length || 0 })}
              </Badge>
            </div>

            {userAds === undefined ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => <AdCardSkeleton key={i} />)}
              </div>
            ) : userAds.length === 0 ? (
              <Card className="bg-muted/30 border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="p-4 rounded-full bg-background mb-4 text-muted-foreground">
                     <PackageIcon className="w-8 h-8 opacity-50" />
                  </div>
                  <h3 className="text-lg font-medium">{t('user.noListings')}</h3>
                  <p className="text-muted-foreground max-w-sm mt-2">
                    {t('user.noListingsDesc', { name: userProfile.name })}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {userAds.map((ad) => {
                  const categoryLabel = ad.categoryId
                    ? t(`categories.${ad.categoryId}`, { defaultValue: ad.categoryId })
                    : t('common.uncategorized', { defaultValue: 'Uncategorized' });
                  const formattedPrice = typeof ad.price === "number"
                    ? `$${ad.price.toLocaleString()}`
                    : typeof ad.minPrice === "number"
                      ? t('user.fromPrice', { price: ad.minPrice.toLocaleString(), defaultValue: `From $${ad.minPrice.toLocaleString()}` })
                      : null;

                  return (
                    <Link key={ad._id} href={`/ads/${ad._id}`}>
                      <a className="block h-full group">
                        <Card className="h-full overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-border/50">
                          <div className="aspect-[4/3] relative overflow-hidden bg-muted">
                            {ad.images && ad.images.length > 0 ? (
                              <img
                                src={ad.images[0]}
                                alt={ad.title}
                                className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full text-muted-foreground">
                                <PackageIcon className="w-12 h-12 opacity-20" />
                              </div>
                            )}
                            {formattedPrice && (
                              <Badge className="absolute top-2 right-2 bg-background/80 hover:bg-background/90 text-foreground backdrop-blur-sm shadow-sm">
                                {formattedPrice}
                              </Badge>
                            )}
                          </div>
                          <CardHeader className="p-4 pb-2">
                            <CardTitle className="text-lg font-semibold line-clamp-1 group-hover:text-primary transition-colors">
                              {ad.title}
                            </CardTitle>
                            <CardDescription className="line-clamp-2 text-xs mt-1">
                              {ad.description}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="p-4 pt-2">
                            <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                              <span>{categoryLabel}</span>
                              <span>{format(new Date(ad._creationTime), 'MMM d, yyyy')}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </a>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function ProfileSkeleton() {
  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1 space-y-6">
            <Card>
              <CardContent className="pt-6 flex flex-col items-center space-y-4">
                <Skeleton className="h-32 w-32 rounded-full" />
                <div className="space-y-2 w-full text-center">
                  <Skeleton className="h-6 w-3/4 mx-auto" />
                  <Skeleton className="h-4 w-1/2 mx-auto" />
                </div>
                <div className="w-full pt-4 space-y-3">
                   <Skeleton className="h-4 w-full" />
                   <Skeleton className="h-4 w-2/3" />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="md:col-span-3 space-y-6">
            <div className="flex justify-between items-center">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-6 w-16" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => <AdCardSkeleton key={i} />)}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function AdCardSkeleton() {
  return (
    <Card className="h-full overflow-hidden">
      <Skeleton className="h-48 w-full" />
      <CardHeader className="p-4">
        <Skeleton className="h-6 w-3/4 mb-2" />
        <Skeleton className="h-4 w-full" />
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex justify-between mt-2">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-1/4" />
        </div>
      </CardContent>
    </Card>
  );
}
